import React, { useRef, useState } from 'react';
import { Box, Button, TextField, Typography, CircularProgress } from '@mui/material';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import { useSnackbar } from 'notistack';
import emailjs from '@emailjs/browser';
import feaBg from '../../assets/global/fea.png';

// ⚠️ استبدل TEMPLATE_ID بالتصريح الخاص بالقالب الجديد الذي ستنشئه للنشرة البريدية
const EMAILJS_SERVICE_ID = "service_4dc117j";
const EMAILJS_TEMPLATE_ID = "template_ydupkb3"; // ضع الـ ID الجديد هنا
const EMAILJS_PUBLIC_KEY = "xt7lYjqGyV_b5EaB5";

const SmartInvestingNewsletter = ({ lng }) => {
    const isAr = lng === 'ar';
    const { enqueueSnackbar } = useSnackbar();
    const formRef = useRef();
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            await emailjs.sendForm(
                EMAILJS_SERVICE_ID,
                EMAILJS_TEMPLATE_ID,
                formRef.current,
                EMAILJS_PUBLIC_KEY
            );
            enqueueSnackbar(
                isAr ? 'تم الاشتراك في النشرة البريدية بنجاح!' : 'Subscribed to newsletter successfully!',
                { variant: 'success' }
            );
            formRef.current.reset();
        } catch (error) {
            console.error('EmailJS Error:', error);
            enqueueSnackbar(
                isAr ? 'حدث خطأ أثناء الاشتراك' : 'An error occurred during subscription',
                { variant: 'error' }
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box>
            <Box
                sx={{
                    position: 'relative',
                    overflow: 'hidden',
                    borderRadius: { xs: '24px', md: '40px' },
                    background: 'linear-gradient(90deg, #1D7ED9 0%, #0F4373 100%)',
                    color: '#FFFFFF',
                    px: { xs: 2.5, sm: 4, md: 8 },
                    py: { xs: 4, sm: 6, md: 7 },
                    boxShadow: '0px 40px 100px rgba(0, 0, 0, 0.4)',
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    alignItems: 'center',
                    justify: 'space-between',
                    gap: 4,
                }}
            >
                {/* Background Image Layer */}
                <Box
                    component="img"
                    src={feaBg}
                    alt="Background"
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        left: isAr ? 'auto' : 0,
                        right: isAr ? 0 : 'auto',
                        height: '100%',
                        objectFit: 'contain',
                        zIndex: 1,
                        opacity: 0.6,
                        mixBlendMode: 'luminosity',
                    }}
                />
                <Box sx={{ flex: 1, position: 'relative', zIndex: 2 }}>
                    <Typography
                        sx={{
                            fontSize: { xs: '14px', md: '16px' },
                            color: 'rgba(255, 255, 255, 0.8)',
                            mb: 1.5,
                            fontWeight: 500,
                        }}
                    >
                        {isAr ? 'الاستثمار الذكي يبدأ من هنا.' : 'Smart investing starts here.'}
                    </Typography>

                    <Typography
                        variant="h2"
                        sx={{
                            fontWeight: 700,
                            fontSize: { xs: '1.8rem', sm: '2.4rem', md: '3.4rem' },
                            lineHeight: 1.3,
                            letterSpacing: '-0.02em',
                            fontFamily: isAr ? 'inherit' : '"Montserrat", "Roboto", "Arial", sans-serif',
                        }}
                    >
                        {isAr ? 'دعنا ننمو معاً' : "Let's grow together"}{' '}
                        <Box component="span" sx={{ display: { xs: 'inline', md: 'block' } }}>
                            {isAr ? 'ابتداءً من اليوم!' : 'starting today!'}
                        </Box>
                    </Typography>
                </Box>

                <Box
                    component="form"
                    ref={formRef}
                    onSubmit={handleSubmit}
                    sx={{
                        width: { xs: '100%', md: 480 },
                        position: 'relative',
                        zIndex: 2,
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            bgcolor: 'rgba(255, 255, 255, 0.15)',
                            backdropFilter: 'blur(10px)',
                            borderRadius: '100px',
                            p: 1,
                            pl: isAr ? 1 : 3,
                            pr: isAr ? 3 : 1,
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                        }}
                    >
                        <TextField
                            required
                            type="email"
                            name="email"
                            fullWidth
                            placeholder={isAr ? 'أدخل بريدك الإلكتروني' : 'Enter Your Email'}
                            variant="standard"
                            InputProps={{
                                disableUnderline: true,
                            }}
                            sx={{
                                '& .MuiInputBase-input': {
                                    color: '#FFFFFF',
                                    fontSize: '15px',
                                    '&::placeholder': {
                                        color: 'rgba(255, 255, 255, 0.6)',
                                        opacity: 1,
                                    },
                                },
                            }}
                        />
                        <Button
                            type="submit"
                            disabled={loading}
                            variant="contained"
                            sx={{
                                minWidth: 54,
                                width: 54,
                                height: 54,
                                borderRadius: '50%',
                                bgcolor: '#3B82F6',
                                color: '#FFFFFF',
                                p: 0,
                                '&:hover': { bgcolor: '#2563EB' },
                                boxShadow: '0px 8px 20px rgba(0, 0, 0, 0.2)',
                            }}
                        >
                            {loading ? (
                                <CircularProgress size={22} sx={{ color: '#FFFFFF' }} />
                            ) : (
                                <SendOutlinedIcon sx={{ fontSize: 22, transform: isAr ? 'rotate(180deg)' : 'none' }} />
                            )}
                        </Button>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default SmartInvestingNewsletter;